/** Estructura cifrada guardada en localStorage: IV + datos, ambos en base64. */
type StoragePayload = {
  /** Vector de inicialización (IV) de AES-GCM, en base64. */
  iv: string;
  /** Texto cifrado, en base64. */
  data: string;
};

/** IndexedDB dedicada SOLO para la clave de cifrado. Independiente de las
 *  demás bases de la app: no se borra al limpiar los datos ni depende de la
 *  versión de ningún otro store. */
const KEY_DB_NAME = "secure";
/** Object store de la IDB de claves. */
const KEY_STORE = "keys";
/** Clave (id) de la `CryptoKey` de sesión dentro del store. */
const KEY_ID = "session";
/** Clave de localStorage donde se guarda el token cifrado. */
const STORAGE_KEY = "session";

/** Almacenamiento seguro del token de sesión. Cifra con AES-GCM usando una clave
 *  aleatoria **no-extraíble** persistida en una IndexedDB dedicada: la clave nunca
 *  se puede leer en bytes (ni con XSS se exfiltra) y un volcado de localStorage
 *  solo revela ciphertext. Al ser aleatoria (no derivada de un fingerprint), es
 *  estable: no provoca logouts por update del navegador, rotación, idioma, etc. */
class SecureStorageService {
  /** Clave AES-GCM cacheada en memoria (no-extraíble). */
  private _key: CryptoKey | null = null;

  // ─── Utilidades base64 ─────────────────────────────────

  /** Convierte un ArrayBuffer a string base64. */
  private bufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /** Convierte un string base64 a un Uint8Array. */
  private base64ToBuffer(base64: string): Uint8Array<ArrayBuffer> {
    const binary = atob(base64);
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      view[i] = binary.charCodeAt(i);
    }
    return view as Uint8Array<ArrayBuffer>;
  }

  /** Genera `length` bytes aleatorios criptográficamente seguros. */
  private randomBytes(length: number): Uint8Array<ArrayBuffer> {
    const buffer = new ArrayBuffer(length);
    const view = new Uint8Array(buffer) as Uint8Array<ArrayBuffer>;
    crypto.getRandomValues(view);
    return view;
  }

  // ─── Clave de cifrado (IndexedDB) ──────────────────────

  /** Abre (creando el store si hace falta) la IDB dedicada de claves. */
  private _openKeyDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(KEY_DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(KEY_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  /** Lee la `CryptoKey` guardada (o undefined si no existe). */
  private async _readKey(): Promise<CryptoKey | undefined> {
    const db = await this._openKeyDb();
    return new Promise((resolve, reject) => {
      const req = db
        .transaction(KEY_STORE, "readonly")
        .objectStore(KEY_STORE)
        .get(KEY_ID);
      req.onsuccess = () => resolve(req.result as CryptoKey | undefined);
      req.onerror = () => reject(req.error);
    });
  }

  /** Persiste la `CryptoKey` en la IDB de claves. */
  private async _writeKey(key: CryptoKey): Promise<void> {
    const db = await this._openKeyDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(KEY_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(KEY_STORE).put(key, KEY_ID);
    });
  }

  /** Borra la `CryptoKey` de la IDB de claves. */
  private async _deleteKey(): Promise<void> {
    const db = await this._openKeyDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(KEY_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(KEY_STORE).delete(KEY_ID);
    });
  }

  /** Retorna la clave AES-GCM no-extraíble: de la caché en memoria, de la IDB, o
   *  genera una nueva (y la persiste) si aún no existe. */
  private async _getOrCreateKey(): Promise<CryptoKey> {
    if (this._key) return this._key;

    const existing = await this._readKey();
    if (existing) return (this._key = existing);

    const key = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false, // no-extraíble: la clave nunca sale en bytes
      ["encrypt", "decrypt"],
    );
    await this._writeKey(key);
    return (this._key = key);
  }

  // ─── Cifrado / descifrado ──────────────────────────────

  /** Cifra (AES-GCM, IV aleatorio) y guarda un valor en localStorage. */
  private async _setItem(value: string): Promise<void> {
    const key = await this._getOrCreateKey();
    const iv = this.randomBytes(12);

    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(value),
    );

    const payload: StoragePayload = {
      iv: this.bufferToBase64(iv.buffer),
      data: this.bufferToBase64(encrypted),
    };

    localStorage.setItem(STORAGE_KEY, btoa(JSON.stringify(payload)));
  }

  /** Lee y descifra el valor de localStorage; si falla el descifrado, lo borra y retorna null. */
  private async _getItem(): Promise<string | null> {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
      const payload: StoragePayload = JSON.parse(atob(raw));
      const key = await this._getOrCreateKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: this.base64ToBuffer(payload.iv) },
        key,
        this.base64ToBuffer(payload.data),
      );
      return new TextDecoder().decode(decrypted);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  // ─── Token de sesión ───────────────────────────────────

  /** Guarda (cifrado) el token de sesión. */
  async set(value: string): Promise<void> {
    return this._setItem(value);
  }

  /** Retorna el token de sesión descifrado (o null si no hay/falla). */
  async get(): Promise<string | null> {
    return this._getItem();
  }

  /** Borra el token de localStorage y la clave de cifrado (cierre de sesión). */
  async remove(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
    this._key = null;
    await this._deleteKey();
  }
}

/** Instancia global del almacenamiento seguro. */
export const secureStorage = new SecureStorageService();
